import { HelpCircle, Pencil, Trash2 } from 'lucide-react';
import { Checkbox } from '@/shared/ui/checkbox';
import ConnexionBadge from '@/shared/components/ConnexionBadge';
import { ICON as TYPE_ICON, LABEL as TYPE_LABEL, COLOR as TYPE_COLOR } from '@/shared/constants/assetTypes';
import {
  dernierPointPvTexte, etatOnduleurTexte, mesureTexte, motifPvTexte,
  productionCouranteTexte,
} from '@/shared/utils/liveness';

/**
 * Carte d'un actif dans la liste du Monitoring.
 *
 * Extraite de `MonitoringPage` pour tenir la limite de 500 lignes par fichier.
 *
 * La pastille de joignabilité est en haut à droite, avant tout chiffre : c'est elle qui
 * dit si les valeurs en dessous décrivent l'instant ou le souvenir.
 *
 * Depuis le lot U, un actif SANS MANDAT SIGNÉ EST LISTÉ ICI COMME LES AUTRES. Il en était
 * purement absent, ce qui faisait passer un enregistrement réussi pour un échec. CETTE
 * MOITIÉ-LÀ DU LOT U TIENT TOUJOURS, et elle doit tenir : le champ `sous_mandat` continue
 * d'arriver du serveur, la carte n'en fait simplement plus rien.
 *
 * ⚠️ LA MARQUE « SANS MANDAT », ELLE, A ÉTÉ RETIRÉE D'ICI LE 10/09 — À LA DEMANDE DE
 * BOUBACAR, ET VOLONTAIREMENT. Ce n'est pas un oubli de remaniement : ne pas la remettre.
 * Le monitoring répond à « cette machine parle-t-elle, et que dit-elle ? ». L'état d'un
 * dossier administratif n'est pas une mesure, et il n'a rien à faire entre deux relevés.
 *
 * ELLE N'A PAS DISPARU DU PRODUIT, elle vit là où elle sert :
 *
 *   `features/pilotage/components/AssetPilotageRow.jsx` — badge + bandeau. C'est l'écran
 *   où l'on AGIT sur la machine, donc le seul où il vaut la peine de nommer le geste qui
 *   débloque : signer le mandat. Le retirer là-bas rouvrirait le défaut du lot U.
 *
 * Le refus, lui, n'a jamais vécu à l'écran : voir `backend/app/core/mandat.py`. Retirer
 * cette marque ne change donc rien à ce qui part sur le matériel.
 *
 * Gardé par `backend/tests/test_a_deux_retouches_ecran.py`, qui exige l'absence ici ET la
 * présence au pilotage — les deux moitiés, sinon le partage se défait par un seul côté.
 */
export default function AssetMonitoringCard({
  asset, isAdmin, isSelected, isChecked, onToggleCheck, onSelect, onEdit, onDelete,
}) {
  const Icon = TYPE_ICON[asset.type] || HelpCircle;

  return (
    <div
      className={`p-3.5 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${
        isSelected ? 'border-neutral-900 ring-1 ring-neutral-900 bg-white'
          : isChecked ? 'border-neutral-400 bg-neutral-50'
          : 'border-neutral-200/80 bg-white'
      }`}
      onClick={onSelect}
      data-testid={`monitoring-card-${asset.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {isAdmin && (
            <span onClick={e => e.stopPropagation()} className="flex items-center">
              <Checkbox
                checked={isChecked}
                onCheckedChange={onToggleCheck}
                data-testid={`select-asset-${asset.id}`}
              />
            </span>
          )}
          <Icon className={`w-4 h-4 ${TYPE_COLOR[asset.type] || 'text-neutral-400'}`} />
          <div>
            <p className="text-[13px] font-medium text-neutral-900">{TYPE_LABEL[asset.type] || asset.type}</p>
            <p className="text-[11px] text-neutral-400">{asset.client_nom}</p>
          </div>
        </div>
        {/* Trois états, avec l'ancienneté : « déconnectée » sans durée ne dit pas s'il
            faut appeler le client ou patienter trente secondes. */}
        <ConnexionBadge
          asset={asset}
          genre={asset.type === 'Batterie' ? 'f' : 'm'}
          className="flex-shrink-0 text-right"
          testId={`monitoring-conn-${asset.id}`}
        />
      </div>

      <div className="space-y-1">
        {asset.type === 'PAC' && (<>
          <MiniMetric label="Etat" value={asset.etat} accent={asset.etat === 'ON' ? 'text-emerald-600' : 'text-neutral-400'} />
          {/* Un thermostat ne mesure PAS sa consommation : aucun de ses datapoints ne
              la porte. « 0 kW » se lisait « il ne consomme rien », alors que la verite
              est « on ne mesure pas ». Meme raison que le SoC d'une batterie jamais vue,
              d'ou le meme `mesureTexte` : deux composants qui inventent chacun leur
              libelle, c'est ce qui a fait croire pendant deux heures qu'une consigne
              etait suivie.
              Au passage, `|| '-'` cachait une vraie mesure de 0 degre. */}
          <MiniMetric label="Conso." value={mesureTexte(asset.consommation_kw, 'kW')} />
          <MiniMetric label="Temp." value={mesureTexte(asset.temperature_mesuree, '°C')} />
        </>)}
        {/* Une centrale sans mesure ne produit pas « 0 kW » : sa production est
            INCONNUE. `|| 0` ecrivait la meme contre-verite que le « 0 kW » de
            consommation d'un thermostat juste au dessus, et pour la meme raison —
            le serveur rend `null`, le front le peignait en zero.
            Second piege, propre au PV : l'API S-Miles ne rend aucune puissance
            instantanee, seulement une serie au pas de quinze minutes. La valeur ne
            s'affiche donc comme COURANTE que si le serveur a pu prouver sa fraicheur ;
            sinon le dernier point connu est montre avec son heure, ce qui ne pretend
            rien. Sans cette distinction, la production du crepuscule s'affichait toute
            la nuit. */}
        {asset.type === 'PV' && (<>
          <MiniMetric label="Production" value={productionCouranteTexte(asset)} accent="text-amber-600" />
          <MiniMetric label="Jour" value={mesureTexte(asset.production_jour_kwh, 'kWh')} />
          <MiniMetric label="Onduleur" value={etatOnduleurTexte(asset)} />
          {!asset.puissance_courante_prouvee && dernierPointPvTexte(asset) && (
            <p className="text-[10px] text-neutral-400" data-testid={`pv-dernier-point-${asset.id}`}>
              Dernier point connu : {dernierPointPvTexte(asset)}
            </p>
          )}
          {motifPvTexte(asset) && (
            <p className="text-[10px] text-amber-600" data-testid={`pv-motif-${asset.id}`}>
              Pas de mesure : {motifPvTexte(asset)}
            </p>
          )}
        </>)}
        {asset.type === 'Ballon' && (<>
          <MiniMetric label="Etat" value={asset.etat} accent={asset.etat === 'ON' ? 'text-emerald-600' : 'text-neutral-400'} />
          <MiniMetric label="Eau" value={`${asset.temperature_eau || '-'}C`} />
        </>)}
        {/* Une batterie sans mesure n'a pas « 0 % de charge » : elle n'a pas de charge
            connue, d'où `mesureTexte`. La ligne « MQTT OK/KO » a disparu — la pastille
            au dessus dit la même chose, en français et avec l'ancienneté. */}
        {asset.type === 'Batterie' && (<>
          <MiniMetric
            label="Etat"
            value={asset.etat && asset.etat !== 'inconnu' ? asset.etat : 'inconnu'}
            accent={asset.etat === 'charge' ? 'text-emerald-600'
              : asset.etat === 'decharge' ? 'text-orange-600' : 'text-neutral-400'}
          />
          <MiniMetric label="SOC" value={mesureTexte(asset.soc_pct, '%')} />
          {asset.secteur?.presence === false && (
            <p className="text-[10px] font-medium text-amber-700" data-testid={`carte-sans-secteur-${asset.id}`}>
              Sans secteur
            </p>
          )}
          <MiniMetric label="Puiss." value={mesureTexte(asset.puissance_kw, 'kW')} />
        </>)}
      </div>

      <p className="text-[10px] text-neutral-300 mt-2">{asset.marque} {asset.modele}</p>
      <div className="flex items-center gap-1 mt-1.5">
        <button onClick={onEdit} className="p-1 rounded text-neutral-300 hover:text-neutral-600 hover:bg-neutral-50" data-testid={`monitoring-edit-${asset.id}`}>
          <Pencil className="w-3 h-3" />
        </button>
        <button onClick={onDelete} className="p-1 rounded text-neutral-300 hover:text-red-500 hover:bg-red-50" data-testid={`monitoring-delete-${asset.id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function MiniMetric({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <span className="text-neutral-400">{label}</span>
      <span className={`font-medium ${accent || 'text-neutral-700'}`}>{value}</span>
    </div>
  );
}
